import { DEFAULT_PERMISSIONS } from '@/constants/constants';
import { isExcelFile, isImageFile, isPdfFile, isTextFile, isVideoFile, isWordFile } from '@/helpers/helpers';
import { Ticket } from '@/Interface/ticketManagementType';
import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionsService } from '@services/permissions.service';
import { UtilityService } from '@services/utility.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, interval, Subscription } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-ticket-detail',
  templateUrl: './ticket-detail.component.html',
  styleUrls: ['./ticket-detail.component.scss']
})
export class TicketDetailComponent implements OnInit, OnDestroy {
  statusBadgeMap: { [key: string]: string } = {
    assigned: 'badge-blue',
    unassigned: 'badge-warning',
    start: 'badge-cyan',
    'in progress': 'badge-blue',
    diagnose: 'badge-yellow',
    'pending material': 'badge-warning',
    'on hold': 'badge-warning',
    resolved: 'badge-success',
    closed: 'badge-gray',
    cancelled: 'badge-danger',
    're-open': 'badge-blue',
    reopen: 'badge-blue'
  };
  @ViewChild('chatBody') chatBody: ElementRef;

  message: string = '';
  selectedFiles: File[] = [];
  loader = true;
  sendingMessage = false;
  currentUserId: number;


  ticketId: number;
  ticketDetail: Ticket | null = null;
  ticketCommunication: any[] = [];

  private pollingSubscription: Subscription;
  private readonly MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes
  private readonly POLLING_INTERVAL = 10000; // 10 seconds
  permissions = { ...DEFAULT_PERMISSIONS };

  permissionsReady: boolean = false;
  constructor(
    private route: ActivatedRoute,
    private utilityService: UtilityService,
    private toastr: ToastrService,
    private router: Router,
    private permService: PermissionsService
  ) { }

  // ngOnInit(): void {
  //   this.ticketId = +this.route.snapshot.paramMap.get('id')!;
  //   const userDetail: any = this.utilityService.decodeUserCode();
  //   this.currentUserId = userDetail?.user_id;
  //   this.loadInitialData();
  //   this.startPolling();
  // }
  ngOnInit(): void {
    this.initComponent()
    const userDetail: any = this.utilityService.decodeUserCode();
    this.currentUserId = userDetail?.user_id;
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }
  async initComponent() {
    this.loader = true
    await this.getComponentPermissions();

    if (this.permissions.canView) {
      // Subscribe to route param changes
      this.route.paramMap.subscribe(params => {
        this.ticketId = +params.get('id')!;

        // Stop existing polling
        if (this.pollingSubscription) {
          this.pollingSubscription.unsubscribe();
        }

        // Reset and reload
        this.ticketDetail = null;
        this.ticketCommunication = [];

        this.loadInitialData();
        this.startPolling();
        // this.loader=false
      });
    }
  }
  async getComponentPermissions() {
    const url = this.permService.getParentUrl();
    // const menuFromLocal = this.permService.loadMenuFromLocalStorage();
    const menuFromLocal = await this.permService.waitForMenuInLocalStorage();

    const matchedMenu = this.permService.findMenuPermissions(menuFromLocal, url);

    this.permissions = matchedMenu
      ? {
        canAdd: !!matchedMenu.canAdd,
        canEdit: !!matchedMenu.canEdit,
        canView: !!matchedMenu.canView,
        canDelete: !!matchedMenu.canDelete
      }
      : { ...DEFAULT_PERMISSIONS };

    this.permissionsReady = true;
  }

  isCurrentUser(userId: number): boolean {
    return userId === this.currentUserId;
  }
  private loadInitialData(): void {
    forkJoin({
      detail: this.utilityService.getTicketDetail(this.ticketId),
      communication: this.utilityService.getTicketCommunication(this.ticketId)
    })
      .pipe(
        finalize(() => {
          this.loader = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.ticketDetail = response.detail;
          this.ticketCommunication = response.communication;
          this.ticketDetail.attachments = this.ticketDetail.attachments.map(file => ({
            ...file,
            type: this.getAttachmentType(file),
            icon: this.getAttachmentIcon(this.getAttachmentType(file))
          }));

          // console.log('Ticket Detail:', this.ticketDetail);
          // console.log('Ticket Communication:', this.ticketCommunication);
          this.scrollToBottom();
        },
        error: (error) => {
          console.error('Error loading ticket data:', error);
        }
      });
  }

  // Add this trackBy function:
  trackByMessageId(index: number, msg: any): number {
    return msg.id; // use whatever unique ID field your messages have
  }
  onFileSelect(event: any): void {
    const files: FileList = event.target.files;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file size
      if (file.size > this.MAX_FILE_SIZE) {
        this.toastr.warning(`File "${file.name}" exceeds the maximum size of 100MB`);
        continue;
      }

      this.selectedFiles.push(file);
    }

    event.target.value = '';
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  sendMessage(): void {
    // Validate message or attachments
    if (!this.message.trim() && this.selectedFiles.length === 0) {
      return;
    }

    const formData = new FormData();
    formData.append('message', this.message.trim());

    this.selectedFiles.forEach((file) => {
      formData.append('attachments', file);
    });

    this.sendingMessage = true;

    this.utilityService.sendMessage(this.ticketId, formData)
      .pipe(
        finalize(() => {
          this.sendingMessage = false;
        })
      )
      .subscribe({
        next: (response) => {
          // console.log('Message sent successfully:', response);

          // Reset form
          this.message = '';
          this.selectedFiles = [];

          // Immediately fetch updated messages
          this.utilityService.getTicketCommunication(this.ticketId).subscribe({
            next: (messages) => {
              this.ticketCommunication = messages;
              this.scrollToBottom();
            }
          });
        },
        error: (error) => {
          console.error('Error sending message:', error);
          this.toastr.error('Failed to send message. Please try again.');
        }
      });
  }

  private scrollToBottom(): void {
    // Scroll to bottom of chat after a short delay to ensure DOM is updated
    setTimeout(() => {
      if (this.chatBody) {
        this.chatBody.nativeElement.scrollTop = this.chatBody.nativeElement.scrollHeight;
      }
    }, 100);
  }

  isAttachmentActivity(message: string): boolean {
    return message?.includes('uploaded attachment(s):');
  }

  extractAttachmentNames(message: string): string[] {
    if (!message) return [];

    const parts = message.split('uploaded attachment(s):');
    if (parts.length < 2) return [];

    return parts[1]
      .split(',')
      .map(name => name.trim())
      .filter(name => name.length > 0);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
  goBack(): void {
    this.router.navigate(['/help-desk/tickets']);
  }
  getAttachmentType(file: any):
    'image' | 'video' | 'pdf' | 'word' | 'excel' | 'text' | 'unknown' {

    if (this.isImageFile(file)) return 'image';
    if (this.isVideoFile(file)) return 'video';
    if (isPdfFile(file)) return 'pdf';
    if (isWordFile(file)) return 'word';
    if (isExcelFile(file)) return 'excel';
    if (isTextFile(file)) return 'text';

    return 'unknown';
  }
  getAttachmentIcon(type: string): string {
    switch (type) {
      case 'image': return 'image';   // use SVG
      case 'pdf': return 'pdf';       // use SVG
      case 'video': return '🎥';
      case 'word': return '📝';
      case 'excel': return '📊';
      case 'text': return '📃';
      default: return '📎';
    }
  }
  isImageFile(file: File | any): boolean {
    if (file instanceof File) {
      return file.type.startsWith('image/');
    }

    if (file?.file_url) {
      return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(file.file_url);
    }

    return false;
  }
  isVideoFile(file: File | any): boolean {
    if (file instanceof File) {
      return file.type.startsWith('video/');
    }

    if (file?.file_url) {
      return /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(file.file_url);
    }

    return false;
  }
  normalizeStatus(status: string): string {
    return status.toLowerCase().trim().replace(/\s+/g, ' ');
  }
  getStatusBadgeClass(status: string): string {
    if (!status) return '';
    return this.statusBadgeMap[this.normalizeStatus(status)] || 'badge-blue';
  }
  // private startPolling(): void {
  //   this.pollingSubscription = interval(this.POLLING_INTERVAL)
  //     .pipe(
  //       switchMap(() => this.utilityService.getTicketCommunication(this.ticketId))
  //     )
  //     .subscribe({
  //       next: (response) => {
  //         // Only update if there are new messages
  //         if (response.length !== this.ticketCommunication.length) {
  //           this.ticketCommunication = response;
  //           this.scrollToBottom();
  //         }
  //       },
  //       error: (error) => {
  //         console.error('Error fetching messages:', error);
  //       }
  //     });
  // }
  private startPolling(): void {
  if (this.pollingSubscription) return; // prevent multiple intervals

  this.pollingSubscription = interval(this.POLLING_INTERVAL)
    .pipe(
      switchMap(() =>
        document.visibilityState === 'visible'
          ? this.utilityService.getTicketCommunication(this.ticketId)
          : []
      )
    )
    .subscribe({
      next: (response: any) => {
        if (!response) return;

        if (response.length !== this.ticketCommunication.length) {
          this.ticketCommunication = response;
          this.scrollToBottom();
        }
      },
      error: (error) => {
        console.error('Error fetching messages:', error);
      }
    });
}
  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined as any;
    }
  }
  private visibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      this.startPolling();
    } else {
      this.stopPolling();
    }
  };
  ngOnDestroy(): void {
  this.stopPolling();
  document.removeEventListener('visibilitychange', this.visibilityHandler);
}
}